/**
 * StatusDistributionCharts
 * Gráficos de pizza para distribuição de status e prioridades
 *
 * Fase 2.2 do Plano de Evolução - Status Distribution Charts
 */

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { PieChart as PieChartIcon, AlertTriangle } from 'lucide-react';
import { ChartTooltipBox, PainelCard, PainelEmpty, PainelStat, PainelStatRow } from './PainelCard';

export interface DistributionItem {
  name: string;
  value: number;
  status?: string;
  priority?: string;
  /** Cor sugerida pela API (hex). A tela usa as cores do tema por status/prioridade. */
  color: string;
  [key: string]: string | number | undefined;
}

export interface StatusDistributionData {
  status: {
    data: DistributionItem[];
    total: number;
  };
  priority: {
    data: DistributionItem[];
    total: number;
  };
}

interface StatusDistributionChartsProps {
  data: StatusDistributionData;
  onSegmentClick?: (type: 'status' | 'priority', item: DistributionItem) => void;
  className?: string;
}

type DistributionType = 'status' | 'priority';

// Pendência/urgência → destructive; andamento → sky; concluídos → escala neutra do tema
const STATUS_COLORS: Record<string, string> = {
  PENDING: 'var(--destructive)',
  ANALYZING: 'var(--chart-1)',
  ANALYZED: 'var(--primary)',
  AUTO_LINKED: 'var(--chart-2)',
  CASE_LINKED: 'var(--chart-3)',
  TASK_CREATED: 'var(--chart-4)',
  ARCHIVED: 'var(--muted-foreground)'
};

const PRIORITY_COLORS: Record<string, string> = {
  NONE: 'var(--border)',
  LOW: 'var(--chart-2)',
  MEDIUM: 'var(--chart-1)',
  HIGH: 'var(--chart-4)',
  URGENT: 'var(--destructive)'
};

const FALLBACK_COLOR = 'var(--chart-5)';

function segmentColor(type: DistributionType, item: DistributionItem): string {
  const key = type === 'status' ? item.status : item.priority;
  const palette = type === 'status' ? STATUS_COLORS : PRIORITY_COLORS;
  return (key && palette[key]) || FALLBACK_COLOR;
}

const countOf = (items: DistributionItem[], field: 'status' | 'priority', key: string) =>
  items.find(d => d[field] === key)?.value || 0;

type LegendEntry = { color?: string; value?: string | number; payload?: { value?: number } };

function renderLegend(props: { payload?: readonly LegendEntry[] }) {
  const { payload = [] } = props;
  return (
    <ul className="mt-2 flex flex-wrap justify-center gap-3">
      {payload.map(entry => (
        <li key={String(entry.value)} className="flex items-center gap-1.5 text-xs">
          <span className="size-3 rounded-xs" style={{ backgroundColor: entry.color }} />
          <span className="text-foreground">{entry.value}</span>
          <span className="font-medium text-muted-foreground">({entry.payload?.value})</span>
        </li>
      ))}
    </ul>
  );
}

export function StatusDistributionCharts({
  data,
  onSegmentClick,
  className = ''
}: StatusDistributionChartsProps) {
  type TooltipProps = { active?: boolean; payload?: Array<{ payload: { name: string; value: number; status?: unknown } }> };
  const DistributionTooltip = ({ active, payload }: TooltipProps) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload;
    const percentage = data.status.total > 0
      ? ((item.value / (item.status ? data.status.total : data.priority.total)) * 100).toFixed(1)
      : '0.0';
    return (
      <ChartTooltipBox>
        <p className="mb-1 font-semibold">{item.name}</p>
        <p className="text-muted-foreground">
          <span className="font-medium">{item.value}</span> publicações
        </p>
        <p className="text-muted-foreground">
          <span className="font-medium">{percentage}%</span> do total
        </p>
      </ChartTooltipBox>
    );
  };

  const handlePieClick = (type: DistributionType, item: DistributionItem | null) => {
    if (onSegmentClick && item) {
      onSegmentClick(type, item);
    }
  };

  const renderPie = (type: DistributionType, title: string, emptyText: string) => {
    const { data: items, total } = data[type];
    return (
      <div className="min-w-0">
        <div className="mb-2 text-center">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          <p className="text-xs text-muted-foreground">
            Total: <span className="font-medium">{total}</span> publicações
          </p>
        </div>

        {items.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={items}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percent }) =>
                  (percent ?? 0) > 0.05 ? `${((percent ?? 0) * 100).toFixed(0)}%` : ''
                }
                outerRadius={70}
                innerRadius={40}
                dataKey="value"
                stroke="var(--card)"
                onClick={(item) => handlePieClick(type, item as unknown as DistributionItem)}
                style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
              >
                {items.map(entry => (
                  <Cell key={`${type}-cell-${entry.name}`} fill={segmentColor(type, entry)} />
                ))}
              </Pie>
              <Tooltip content={<DistributionTooltip />} />
              <Legend content={renderLegend} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[220px] items-center justify-center">
            <PainelEmpty icon={AlertTriangle}>{emptyText}</PainelEmpty>
          </div>
        )}
      </div>
    );
  };

  const statusItems = data.status.data;
  const priorityItems = data.priority.data;
  const processed = ['ANALYZED', 'CASE_LINKED', 'TASK_CREATED', 'ARCHIVED']
    .reduce((sum, status) => sum + countOf(statusItems, 'status', status), 0);

  return (
    <PainelCard title="Distribuição de Status e Prioridades" icon={PieChartIcon} className={className}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {renderPie('status', 'Status de Análise', 'Sem dados de status')}
        {renderPie('priority', 'Nível de Prioridade', 'Sem dados de prioridade')}
      </div>

      <PainelStatRow>
        <PainelStat label="Pendentes" value={countOf(statusItems, 'status', 'PENDING')} tone="destructive" />
        <PainelStat label="Analisadas" value={countOf(statusItems, 'status', 'ANALYZED')} tone="primary" />
        <PainelStat
          label="Alta/Urgente"
          value={countOf(priorityItems, 'priority', 'HIGH') + countOf(priorityItems, 'priority', 'URGENT')}
          tone="destructive"
        />
        <PainelStat
          label="Taxa Processamento"
          value={`${data.status.total > 0 ? Math.round((processed / data.status.total) * 100) : 0}%`}
        />
      </PainelStatRow>
    </PainelCard>
  );
}
